import axios from 'axios'
// import qs from 'qs'
import { toast } from 'vue-sonner'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosRequestConfig {
    retry?: boolean
    retryCount?: number
  }
}

const api = axios.create({
  baseURL: (import.meta.env.DEV && import.meta.env.VITE_OPEN_PROXY) ? '/proxy/' : import.meta.env.VITE_APP_API_BASEURL,
  timeout: 1000 * 60,
  responseType: 'json',
})

api.interceptors.request.use(
  (request) => {
    // 全局拦截请求发送前提交的参数
    const settingsStore = useSettingsStore()
    const userStore = useUserStore()
    // 设置请求头
    if (request.headers) {
      request.headers['Accept-Language'] = settingsStore.lang
      // 检查是否有 token（无论是否已登录，只要有 token 就添加）
      const token = userStore.token || localStorage.getItem('token') || ''
      // 更新baseUrl
      const baseUrl = localStorage.getItem('baseUrl') || ''
      if (baseUrl) {
        request.baseURL = baseUrl
      }
      if (token) {
        // 除了搜索影片接口外，其他接口都需要添加 cms_token 请求头
        // 搜索影片接口是固定地址：https://gzxh.ruiziyou.com/conf/film/queryFilmsOption，不需要token
        const url = request.url || ''
        const isSearchMovieApi = url.includes('queryFilmsOption') || url.includes('searchMovie') || url.includes('search_movie')
        if (!isSearchMovieApi) {
          request.headers.cms_token = token
        }
        else {
          // 搜索影片接口明确不添加token
          delete request.headers.Token
          delete request.headers.cms_token
        }
      }
    }
    // 是否将 POST 请求参数进行字符串化处理
    if (request.method === 'post') {
      // request.data = qs.stringify(request.data, {
      //   arrayFormat: 'brackets',
      // })
    }
    return request
  },
)

// 处理错误信息的函数
function handleError(error: any) {
  if (error.status === 401) {
    useUserStore().requestLogout()
  }
  else {
    let message = error.message
    if (message === 'Network Error') {
      message = '后端网络故障'
    }
    else if (message.includes('timeout')) {
      message = '接口请求超时'
    }
    else if (message.includes('Request failed with status code')) {
      message = `接口${message.substr(message.length - 3)}异常`
    }
    toast.error('Error', {
      description: message,
    })
  }
  return Promise.reject(error)
}

api.interceptors.response.use(
  (response) => {
    /**
     * 全局拦截请求发送后返回的数据，如果数据有报错则在这做全局的错误提示
     * 支持两种响应格式：
     * 1. 旧格式：{ status: 1, error: '', data: {} }
     * 2. 新格式：{ errcode: 0, msg: 'ok', data: {} }
     * 规则是当 status 为 1 或 errcode 为 0 时表示请求成功
     * 请求出错时 error/msg 会返回错误信息
     */
    if (typeof response.data === 'object') {
      // 新格式：{ errcode: 0, msg: 'ok', data: {} }
      if (response.data.errcode !== undefined) {
        if (response.data.errcode === 0) {
          // 成功，但可能有警告信息
          if (response.data.msg && response.data.msg !== 'ok') {
            toast.warning('Warning', {
              description: response.data.msg,
            })
          }
          return Promise.resolve(response.data)
        }
        else {
          // 错误
          toast.error('Error', {
            description: response.data.msg || '请求失败',
          })
          // 如果是认证错误，可能需要登出
          if (response.data.errcode === 401 || response.data.errcode === 403) {
            useUserStore().requestLogout()
          }
          return Promise.reject(response.data)
        }
      }
      // 旧格式：{ status: 1, error: '', data: {} }
      else if (response.data.status !== undefined) {
        if (response.data.status === 1) {
          if (response.data.error !== '') {
            toast.warning('Warning', {
              description: response.data.error,
            })
            return Promise.reject(response.data)
          }
        }
        else {
          useUserStore().requestLogout()
        }
        return Promise.resolve(response.data)
      }
      // 其他格式直接返回
      else {
        return Promise.resolve(response.data)
      }
    }
    else {
      return Promise.reject(response.data)
    }
  },
  async (error) => {
    // 获取请求配置
    const config = error.config
    // 如果配置不存在或未启用重试，则直接处理错误
    if (!config || !config.retry) {
      return handleError(error)
    }
    // 设置重试次数
    config.retryCount = config.retryCount || 0
    // 判断是否超过重试次数
    if (config.retryCount >= MAX_RETRY_COUNT) {
      return handleError(error)
    }
    // 重试次数自增
    config.retryCount += 1
    // 延迟重试
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
    // 重新发起请求
    return api(config)
  },
)

export default api
